import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Axios instance pre-configured with the API base URL and JSON headers.
 */
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// ============================================
// Request interceptor — attach JWT access token
// ============================================
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// Response interceptor — handle 401 with token refresh
// ============================================
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't already retried this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request until the token refresh resolves
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        // No refresh token available — force logout
        _clearAuth();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        localStorage.setItem('access_token', access);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`;

        processQueue(null, access);
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        _clearAuth();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function _clearAuth() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  window.location.href = '/login';
}

// ============================================
// Auth API
// ============================================

export const authAPI = {
  register: (userData) =>
    apiClient.post('/auth/register/', userData),

  login: (credentials) =>
    apiClient.post('/auth/login/', credentials),

  logout: (refreshToken) => {
    const promise = refreshToken
      ? apiClient.post('/auth/logout/', { refresh: refreshToken })
      : Promise.resolve();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    return promise;
  },

  refreshToken: (refresh) =>
    apiClient.post('/auth/refresh/', { refresh }),

  getProfile: () =>
    apiClient.get('/auth/profile/'),

  updateProfile: (userData) =>
    apiClient.patch('/auth/profile/', userData),

  changePassword: (data) =>
    apiClient.post('/auth/change-password/', data),
};

// ============================================
// Goals API
// ============================================

export const goalsAPI = {
  list: (params = {}) =>
    apiClient.get('/goals/', { params }),

  create: (goalData) =>
    apiClient.post('/goals/', goalData),

  get: (id) =>
    apiClient.get(`/goals/${id}/`),

  update: (id, goalData) =>
    apiClient.put(`/goals/${id}/`, goalData),

  partialUpdate: (id, goalData) =>
    apiClient.patch(`/goals/${id}/`, goalData),

  delete: (id) =>
    apiClient.delete(`/goals/${id}/`),

  getProgress: (id) =>
    apiClient.get(`/goals/${id}/progress/`),

  getSummary: () =>
    apiClient.get('/goals/summary/'),
};

// ============================================
// Transactions API
// ============================================

export const transactionsAPI = {
  list: (params = {}) =>
    apiClient.get('/transactions/', { params }),

  create: (transactionData) =>
    apiClient.post('/transactions/', transactionData),

  get: (id) =>
    apiClient.get(`/transactions/${id}/`),

  update: (id, transactionData) =>
    apiClient.put(`/transactions/${id}/`, transactionData),

  partialUpdate: (id, transactionData) =>
    apiClient.patch(`/transactions/${id}/`, transactionData),

  delete: (id) =>
    apiClient.delete(`/transactions/${id}/`),

  getByGoal: (goalId, params = {}) =>
    apiClient.get(`/transactions/goal/${goalId}/`, { params }),

  getRecent: (limit = 10) =>
    apiClient.get('/transactions/recent/', { params: { limit } }),
};

// ============================================
// Brokers API
// ============================================

export const brokersAPI = {
  list: (params = {}) =>
    apiClient.get('/brokers/', { params }),

  create: (brokerData) =>
    apiClient.post('/brokers/', brokerData),

  get: (id) =>
    apiClient.get(`/brokers/${id}/`),

  update: (id, brokerData) =>
    apiClient.put(`/brokers/${id}/`, brokerData),

  partialUpdate: (id, brokerData) =>
    apiClient.patch(`/brokers/${id}/`, brokerData),

  delete: (id) =>
    apiClient.delete(`/brokers/${id}/`),

  getPortfolio: (id) =>
    apiClient.get(`/brokers/${id}/portfolio/`),
};

// ============================================
// Assets API
// ============================================

export const assetsAPI = {
  list: (params = {}) =>
    apiClient.get('/assets/', { params }),

  create: (assetData) =>
    apiClient.post('/assets/', assetData),

  get: (id) =>
    apiClient.get(`/assets/${id}/`),

  update: (id, assetData) =>
    apiClient.put(`/assets/${id}/`, assetData),

  delete: (id) =>
    apiClient.delete(`/assets/${id}/`),

  search: (query) =>
    apiClient.get('/assets/search/', { params: { q: query } }),

  getPriceHistory: (id) =>
    apiClient.get(`/assets/${id}/price-history/`),
};

// ============================================
// Portfolio API
// ============================================

export const portfolioAPI = {
  list: (params = {}) =>
    apiClient.get('/portfolio/', { params }),

  getSummary: () =>
    apiClient.get('/portfolio/summary/'),

  byGoal: () =>
    apiClient.get('/portfolio/by-goal/'),

  getPerformance: () =>
    apiClient.get('/portfolio/performance/'),

  updatePrice: (id, currentPrice) =>
    apiClient.patch(`/portfolio/${id}/update-price/`, { current_price: currentPrice }),
};

// ============================================
// Dashboard API
// ============================================

export const dashboardAPI = {
  getData: () =>
    apiClient.get('/dashboard/'),

  getMetrics: () =>
    apiClient.get('/dashboard/metrics/'),
};

// ============================================
// Price History API
// ============================================

export const priceHistoryAPI = {
  list: (assetId) =>
    apiClient.get('/price-history/', { params: { asset: assetId } }),

  create: (data) =>
    apiClient.post('/price-history/', data),

  delete: (id) =>
    apiClient.delete(`/price-history/${id}/`),
};

// ============================================
// Cash Movements API
// ============================================

export const cashMovementsAPI = {
  list: (params = {}) => apiClient.get('/cash-movements/', { params }),
  create: (data) => apiClient.post('/cash-movements/', data),
  delete: (id) => apiClient.delete(`/cash-movements/${id}/`),
};

export default apiClient;
